import { Component, OnInit } from '@angular/core';
import { PaymentService } from '../../services/payment.service';
import { CartModel } from '../../models/cart.model';
import { LanguageService } from 'src/app/services/language.service';
import { LanguageEnum } from 'src/app/shared/models/item.model';
import { environment } from 'src/environments/environment';
import { IdentityService } from 'src/app/@core/services/identity.service';
import { IdentityModel } from 'src/app/@core/models/identity.model';
import { CreateOrderModel } from '../../models/create-order.model';
import { Router } from '@angular/router';
import { CouponResponseModel } from '../../models/coupon-response.model';
import { TranslateService } from '@ngx-translate/core';
import { StudentEnrollmentService } from 'src/app/course/services/student-enrollment.service';
import { CourseReferenceType } from 'src/app/course/enums/enrollment/course-reference-type.enum';
import { StudentEnrollmentType } from 'src/app/course/enums/enrollment/student-enrollment-type.enum';
import { AuthService } from 'src/app/auth/auth.service';
import { ToastrService } from 'ngx-toastr';
import { SwalService } from 'src/app/shared/services/swal.service';

@Component({
  selector: 'app-check-out',
  templateUrl: './check-out.component.html',
  styleUrls: ['./check-out.component.scss']
})
export class CheckOutComponent implements OnInit {
  cartItems: CartModel[] = [];
  orderRequest: CreateOrderModel = {
    cartItemIds: [],
    couponId: 0
  };
  userId: number;
  identityModel: IdentityModel;
  vatAmount: number = 0;
  subTotal: number = 0;
  totalAfterDiscount: number = 0;
  total: number = 0;
  coupon: string = '';
  couponResponse: CouponResponseModel;
  couponDiscount: number;
  couponErrorMessage: string = '';
  isRtl: boolean = true;
  resources = environment.courseResource;
  /** Set when the order/enrolment/payment-URL step fails; renders a recoverable alert above the checkout button. */
  paymentError: boolean = false;

  constructor(private paymentService: PaymentService,
    private langService: LanguageService,
    private identityService: IdentityService,
    private translateService: TranslateService,
    private studentEnrollmentService: StudentEnrollmentService,
    private authService: AuthService,
    private toastr: ToastrService,
    private swalService: SwalService,
    private router: Router) { }

  get isUnified(): boolean {
    return this.identityService.isUnifiedCode();
  }

  ngOnInit() {
    if (!this.authService.isAuthenticated()) this.router.navigate(['/join/login']);

    this.isRtl = this.langService.getCurrentLanguage() === LanguageEnum.ar;
    this.langService.languageChange.subscribe((result: LanguageEnum) => {
      this.isRtl = result === LanguageEnum.ar;
    });

    this.identityModel = this.identityService.getIdentityModel();
    this.getUserCart();

    this.authService.currentUser.subscribe(user => {
      this.userId = user.id;
    });
  }

  getUserCart() {
    this.paymentService.getCart().subscribe((result: CartModel[]) => {
      this.cartItems = result;
      result.length > 0 ? this.calculateCartItems() : this.router.navigate(['/course']);
    });
  }

  calculateCartItems() {
    this.subTotal = 0;
    this.total = 0;
    this.totalAfterDiscount = 0;

    this.cartItems.map((item: CartModel) => {
      if (item.discount > 0)
        item.priceAfterDiscount =
          Number((item.price - (item.price * (item.discount / 100))).toFixed(1));
      else item.priceAfterDiscount = item.price;

      this.subTotal += item.price;
      this.totalAfterDiscount += item.priceAfterDiscount;
    });

    if (this.identityModel.vat > 0) {
      this.vatAmount = this.subTotal * (this.identityModel.vat / 100);
    }

    this.total = this.totalAfterDiscount + this.vatAmount;

    this.calcCouponDiscount();
  }

  applyCoupon() {
    if (this.coupon == '') return;

    this.paymentService.validateCoupon(this.coupon).subscribe((result: CouponResponseModel) => {
      this.couponResponse = result;
      this.couponErrorMessage = '';
      this.calcCouponDiscount();
    }, (error: any) => {
      this.couponErrorMessage = this.translateService.instant('course.invalidCoupon');
    });
  }

  calcCouponDiscount() {
    if (this.couponResponse?.discount > 0) {
      this.couponDiscount = this.total * (this.couponResponse.discount / 100);
      this.total = this.total - this.couponDiscount;
    }
  }

  /** Asks for confirmation (mode-aware SweetAlert) before removing the item from the cart. */
  deleteCartItem(item: CartModel) {
    this.swalService.customConfirmation(
      this.translateService.instant('actions.delete_title'),
      this.translateService.instant('course.checkoutCart.deleteItemText'),
      this.translateService.instant('actions.delete_confirm')
    ).then((result) => {
      if (result.isConfirmed) this.removeCartItem(item);
    });
  }

  private removeCartItem(item: CartModel) {
    this.cartItems = this.cartItems.filter(c => c.id != item.id);
    var body = {
      cartId: item.id
    };

    this.paymentService.removeFromCart(body).subscribe(() => {
      this.paymentService.itemsInCart = this.cartItems.length;
      if (this.cartItems.length == 0) this.router.navigate(['/course']);

      else this.calculateCartItems();
    });
  }

  isLoading: boolean = false;
  async createOrder() {
    this.orderRequest.cartItemIds = this.cartItems.map((c) => c.id)
    this.orderRequest.couponId = this.couponResponse ? this.couponResponse.id : 0;
    this.isLoading = true;
    this.paymentError = false;

    this.paymentService.createOrder(this.orderRequest).subscribe((result: number[]) => {
      if (!result?.length) {
        this.onPaymentFailed();
        return;
      }
      result.forEach(element => {
        this.studentEnrollmentService.assign(CourseReferenceType.Course, element, [this.userId],
          StudentEnrollmentType.Enrollment).subscribe(async () => {
            this.paymentService.itemsInCart = 0;

            let lastElement = result[result.length - 1];
            if (element === lastElement) {
              await this.createPayment(element);
              // this.router.navigate(['/myspace/courses']);
              // this.toastr.success(this.translateService.instant('course.successPayment'),
              //   this.translateService.instant('profile.status.success'));
            }

          }, () => this.onPaymentFailed());
      });
    }, () => this.onPaymentFailed());
  }

  async createPayment(courseId: number) {
    let paymentURL: string;
    try {
      paymentURL = await this.paymentService.createPayment(this.userId, this.total.toFixed(2), courseId);
    } catch {
      paymentURL = '';
    }

    if (paymentURL) {
      this.redirectToPayment(paymentURL);
    } else {
      this.onPaymentFailed();
    }
  }

  /** Full-page redirect to the payment provider; a separate method so specs can observe it without leaving the page. */
  redirectToPayment(paymentURL: string) {
    window.location.href = paymentURL;
  }

  /** Recoverable failure: stop the spinner, show the alert and keep the cart on screen (no navigation). */
  private onPaymentFailed() {
    this.isLoading = false;
    this.paymentError = true;
  }
}
